<template>
  <!-- CRM replica of client StepRegulatory.vue — the Boomerang "use_custom_terms"
       layout, which is the branch the profile texts actually drive (AGB heading,
       legal links + URLs, consent text, name disclaimer, insurance confirm).
       Nezasa-rendered terms branches carry no profile text and are omitted. -->
  <div class="step-regulatory">
    <h2 class="step-title">{{ titleText }}</h2>
    <p class="step-subtitle">{{ subtitleText }}</p>

    <!-- Insurance confirmation text (insurance_confirm box — appears on Options step,
         shown here so its toggle has a visible effect) -->
    <div v-if="showInsuranceConfirm && insuranceConfirmHtml" class="regulatory-section">
      <div class="regulatory-intro" v-html="insuranceConfirmHtml"></div>
    </div>

    <!-- Name disclaimer (name_disclaimer box — appears on Travelers step) -->
    <div v-if="showNameDisclaimer && (nameHeading || nameConfirm || nameTextHtml)" class="regulatory-section">
      <h3 class="regulatory-heading" v-if="nameHeading">{{ nameHeading }}</h3>
      <div class="regulatory-intro" v-if="nameTextHtml" v-html="nameTextHtml"></div>
      <label class="checkbox-label" v-if="nameConfirm">
        <span class="toggle" :style="toggleStyle">
          <input type="checkbox" />
          <span class="toggle-slider"></span>
        </span>
        <span>{{ nameConfirm }}</span>
      </label>
    </div>

    <!-- Visa / entry requirements (passolution box) -->
    <div v-if="showPassolution" class="regulatory-section">
      <h3 class="regulatory-heading">{{ travelInfoHeading }}</h3>
      <div class="regulatory-intro" v-html="travelInfoIntro"></div>
      <label class="checkbox-label">
        <span class="toggle" :style="toggleStyle">
          <input type="checkbox" />
          <span class="toggle-slider"></span>
        </span>
        <span v-html="travelInfoConfirm"></span>
      </label>
    </div>

    <!-- AGB / Datenschutz / Formblatt / Insolvenz link list + consent (custom_terms box) -->
    <div v-if="showCustomTerms" class="regulatory-section">
      <h3 class="regulatory-heading">{{ agbHeading }}</h3>
      <ul class="boomerang-agb-list">
        <li v-if="links.agb"><a>{{ links.agb }}</a></li>
        <li v-if="links.formblatt"><a>{{ links.formblatt }}</a></li>
        <li v-if="links.insolvenz"><a>{{ links.insolvenz }}</a></li>
        <li v-if="links.datenschutz"><a>{{ links.datenschutz }}</a></li>
        <li v-if="!hasAnyLink" class="boomerang-agb-empty">{{ noLinksText }}</li>
      </ul>
      <label class="checkbox-label" v-if="agbConfirmHtml">
        <span class="toggle" :style="toggleStyle">
          <input type="checkbox" />
          <span class="toggle-slider"></span>
        </span>
        <span v-html="agbConfirmHtml"></span>
      </label>
    </div>

    <div class="step-actions">
      <button class="checkout-btn checkout-btn--secondary">{{ backText }}</button>
      <button class="checkout-btn checkout-btn--primary" :style="{ backgroundColor: brandColor }">{{ proceedText }}</button>
    </div>
  </div>
</template>

<script>
import previewLang from './previewLang';

export default {
  name: 'TermsPreview',
  mixins: [previewLang],
  props: {
    texts: { type: Object, default: () => ({}) },
    rich: { type: Object, default: () => ({}) },
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' },
    // Per-box visibility from the profile; default both boxes on so an unset profile
    // looks like the standard layout.
    boxVisibility: { type: Object, default: () => ({}) }
  },
  computed: {
    toggleStyle() { return { '--toggle-on': this.brandColor }; },
    // passolution defaults ON; the rest default OFF (matches global defaults / profile).
    showPassolution() { return this.boxVisibility.passolution !== false && this.boxVisibility.passolution !== 0; },
    showCustomTerms() { return this.boxVisibility.custom_terms === true || this.boxVisibility.custom_terms === 1; },
    showNameDisclaimer() { return this.boxVisibility.name_disclaimer === true || this.boxVisibility.name_disclaimer === 1; },
    showInsuranceConfirm() { return this.boxVisibility.insurance_confirm === true || this.boxVisibility.insurance_confirm === 1; },
    travelInfoHeading() { return this.t('checkout_travelinfo_title', 'Hinweise Visa-, Einreise- und Impfbestimmungen'); },
    travelInfoIntro() { return this.t('checkout_travelinfo_intro', '<p>Bitte beachten Sie die aktuell gültigen Visa-, Einreise- und Impfbestimmungen Ihrer Reise.</p>'); },
    travelInfoConfirm() { return this.t('checkout_travelinfo_checkbox', '<p>Ich habe die Visa-, Einreise- und Impfbestimmungen zur Kenntnis genommen und bin mir bewusst, dass ich für die erforderlichen Dokumente und Maßnahmen selbst verantwortlich bin.</p>'); },
    titleText() { return this.t('checkout_terms_title', 'Terms & Conditions'); },
    subtitleText() { return this.t('checkout_terms_subtitle', 'Please review and accept the following.'); },

    agbHeading() {
      return (this.texts && this.texts.checkout_agb_heading)
        || this.t('boomerang_agb_heading', 'Allg. Geschäftsbedingungen und Datenschutz');
    },
    links() {
      const x = this.texts || {};
      return {
        agb: x.checkout_agb_link || '',
        formblatt: x.checkout_formblatt_link || '',
        insolvenz: x.checkout_insolvenz_link || '',
        datenschutz: x.checkout_datenschutz_link || ''
      };
    },
    hasAnyLink() {
      const l = this.links;
      return !!(l.agb || l.formblatt || l.insolvenz || l.datenschutz);
    },
    noLinksText() { return this.t('checkout_preview_no_links', '(No legal links configured yet)'); },
    // Mirrors the client fallback chain (StepRegulatory): profile rich text,
    // else the locale's boomerang_agb_confirm.
    agbConfirmHtml() { return (this.rich && this.rich.checkout_agb_confirm) || this.t('boomerang_agb_confirm', ''); },
    insuranceConfirmHtml() { return (this.rich && this.rich.checkout_insurance_confirm) || ''; },
    nameHeading() { return (this.texts && this.texts.checkout_name_heading) || ''; },
    nameConfirm() { return (this.texts && this.texts.checkout_name_confirm) || ''; },
    nameTextHtml() { return (this.rich && this.rich.checkout_name_text) || ''; },
    backText() { return this.t('checkout_back', 'Back'); },
    proceedText() { return this.t('checkout_proceed_payment', 'Proceed to Payment'); }
  }
};
</script>

<style scoped>
.step-title { font-size: 20px; font-weight: 700; margin: 0 0 6px; }
.step-subtitle { font-size: 13px; color: #666; margin: 0 0 24px; }

.regulatory-section {
  margin-bottom: 24px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
}
.regulatory-heading { font-size: 15px; font-weight: 600; margin: 0 0 8px; }
.regulatory-intro { font-size: 13px; color: #555; margin: 0 0 12px; }

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.4;
  margin-top: 8px;
}
.checkbox-label input { margin: 0; flex-shrink: 0; }

.toggle { position: relative; display: inline-block; width: 38px; height: 22px; flex-shrink: 0; }
.toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-slider { position: absolute; inset: 0; background-color: #ccc; border-radius: 22px; }
.toggle-slider::before {
  content: ''; position: absolute; height: 16px; width: 16px; left: 3px; top: 3px;
  background-color: #fff; border-radius: 50%; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.boomerang-agb-list { list-style: disc; padding-left: 1.4rem; margin: 0 0 14px; }
.boomerang-agb-list li { margin: 4px 0; font-size: 13px; }
.boomerang-agb-list a { color: #1a73e8; text-decoration: underline; }
.boomerang-agb-empty { list-style: none; color: #aaa; font-style: italic; }

.step-actions { margin-top: 28px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.checkout-btn { padding: 10px 28px; border: none; border-radius: 4px; font-size: 14px; font-weight: 600; }
.checkout-btn--primary { color: #fff; }
.checkout-btn--secondary { background: #f0f0f0; color: #333; }

.regulatory-intro :deep(p), .checkbox-label > span :deep(p) { margin: 0 0 6px; display: inline; }
</style>
